// components/Navbar/DesktopNavbar.js
"use client";
import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from "../components/LanguageSelector";
import SearchResults from "./SearchResults";

const DesktopNavbar = ({ searchProps,t }) => {
    const {  isHydrated } = useLanguage();
    const router = useRouter();
    const [isCallOpen, setIsCallOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const callRef = useRef(null);
    const searchRef = useRef(null);

    const { searchQuery, handleSearchChange, handleInputFocus, isLoading } = searchProps;

    // Click outside yopish - faqat close button uchun
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Call dropdown uchun - faqat tashqi zonada bosilganda yopish
            if (callRef.current && !callRef.current.contains(event.target)) {
                setIsCallOpen(false);
            }
        };

        if (isCallOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('touchstart', handleClickOutside);
            };
        }
    }, [isCallOpen]);

    // Body scroll ni boshqarish
    useEffect(() => {
        if (isSearchOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isSearchOpen]);

    // Hydration tugashini kutish
    if (!isHydrated) {
        return (
            <div className="hidden md:flex items-center gap-4">
                {/* Loading skeleton */}
                <div className="animate-pulse flex gap-4">
                    <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
                    <div className="h-10 bg-gray-200 rounded-lg w-24"></div>
                    <div className="h-10 bg-gray-200 rounded-lg w-10"></div>
                    <div className="h-10 bg-gray-200 rounded-lg w-10"></div>
                </div>
            </div>
        );
    }

    // Phone icon component
    const PhoneIcon = ({ className }) => (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    // Telegram icon component
    const TelegramIcon = ({ className }) => (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
    );

    const handlePhoneClick = (number) => {
        // Telegramga yuborish
        const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(number)}&text=${encodeURIComponent(`${t.phone_number}: ${number}`)}`;
        window.open(telegramUrl, '_blank');
        setIsCallOpen(false);
    };

    // Category click handler
    const handleCategoryClick = (categoryId) => {
        setIsSearchOpen(false);
        router.push(`/cattegoryDatail/${categoryId}`);
    };

    // Search overlay yopish funksiyasi - faqat orqa fon bosilganda
    const handleSearchOverlayClick = (e) => {
        // Faqat overlay background bosilganda yopish
        if (e.target.classList.contains('search-overlay-bg')) {
            setIsSearchOpen(false);
        }
    };

    // Call dropdown ichida bosilganda yopilmaslik uchun
    const handleCallDropdownClick = (e) => {
        e.stopPropagation();
    };

    // Search content ichida bosilganda yopilmaslik uchun
    const handleSearchContentClick = (e) => {
        e.stopPropagation();
    };

    return (
        <>
            {/* Navigation Menu */}
            <ul className="hidden md:flex gap-4 text-white rounded-lg py-2 bg-white/10 backdrop-blur-md p-2.5 border border-white/20 hover:bg-white/15 transition-all duration-200">
                <li>
                    <Link href="/" className="duration-75 hover:text-red-600">
                        {t.home}
                    </Link>
                </li>
                <li>
                    <Link href="/about" className="duration-75 hover:text-red-600">
                        {t.about}
                    </Link>
                </li>
                <li>
                    <Link href="/cattegory" className="duration-75 hover:text-red-600">
                        {t.categories}
                    </Link>
                </li>
                <li>
                    <Link href="/contact" className="duration-150 hover:text-red-600">
                        {t.contact_us}
                    </Link>
                </li>
            </ul>

            {/* Right Side Actions */}
            <div className="hidden md:flex items-center gap-4">
                {/* Language Selector */}
                <LanguageSelector
                    variant="navbar"
                    className=""
                />

                {/* Action Buttons */}
                <div className="flex gap-4">
                    {/* Call Button with Dropdown */}
                    <div className="relative" ref={callRef}>
                        <button
                            className="bg-white/10 backdrop-blur-md p-2.5 rounded-lg border border-white/20 hover:bg-white/15 transition-all duration-200"
                            onClick={() => setIsCallOpen(!isCallOpen)}
                        >
                            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="currentColor"
                                />
                            </svg>
                        </button>

                        {/* Call Dropdown Menu */}
                        {isCallOpen && (
                            <div
                                className="absolute right-0 top-full mt-2 min-w-[300px]"
                                style={{ zIndex: 10000 }}
                                onClick={handleCallDropdownClick}
                            >
                                <div className="bg-white rounded-2xl p-6 shadow-lg border border-green-200 hover:shadow-xl transition-all duration-200">
                                    {/* Close button */}
                                    <div className="flex justify-end mb-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsCallOpen(false);
                                            }}
                                            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-all duration-200"
                                        >
                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                                                <path
                                                    d="M6 18L18 6M6 6l12 12"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                    
                                    <div className="flex items-start gap-4">
                                        <div className="bg-green-100 p-3 rounded-xl flex-shrink-0">
                                            <PhoneIcon className="w-6 h-6 text-green-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-bold text-gray-900 text-lg mb-1">
                                                {t.contact_us}
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-3">{t.contact_message}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-3 flex flex-col justify-center items-center">
                                        <button
                                            onClick={() => handlePhoneClick('+998983095550')}
                                            className="w-full text-gray-700 hover:text-blue-600 transition-colors font-medium hover:bg-blue-50 p-3 rounded-lg flex items-center gap-3 group"
                                        >
                                            <TelegramIcon className="w-5 h-5 text-blue-500 group-hover:text-blue-600" />
                                            <span>+998 98 309 55 50</span>
                                        </button>
                                        <button
                                            onClick={() => handlePhoneClick('+998949876000')}
                                            className="w-full text-gray-700 hover:text-blue-600 transition-colors font-medium hover:bg-blue-50 p-3 rounded-lg flex items-center gap-3 group"
                                        >
                                            <TelegramIcon className="w-5 h-5 text-blue-500 group-hover:text-blue-600" />
                                            <span>+998 94 987 60 00</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Search Button */}
                    <div className="relative" ref={searchRef}>
                        <button
                            className="bg-white/10 backdrop-blur-md p-2.5 rounded-lg border border-white/20 hover:bg-white/15 transition-all duration-200"
                            onClick={() => setIsSearchOpen(!isSearchOpen)}
                        >
                            {isSearchOpen ? (
                                // Cancel/Close icon
                                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M6 18L18 6M6 6l12 12"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            ) : (
                                // Search icon
                                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Full Width Search Overlay */}
            {isSearchOpen && (
                <div
                    className="search-overlay-bg fixed inset-0 bg-black/30 backdrop-blur-sm z-[10000]"
                    onClick={handleSearchOverlayClick}
                >
                    <div className="w-full h-full flex flex-col">
                        {/* Top section with close button */}
                        <div className="flex justify-end p-4">
                            <button
                                onClick={() => setIsSearchOpen(false)}
                                className="bg-white/10 backdrop-blur-md p-2.5 rounded-lg border border-white/20 hover:bg-white/15 transition-all duration-200"
                            >
                                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M6 18L18 6M6 6l12 12"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>

                        {/* Main content area */}
                        <div
                            className="flex-1 bg-gradient-to-b from-black/40 via-black/20 to-transparent backdrop-blur-[60px] overflow-y-auto"
                            style={{
                                animation: 'slideDown 0.3s ease-out'
                            }}
                            onClick={handleSearchContentClick}
                        >
                            {/* Search Input */}
                            <div 
                                className="flex items-center gap-3 bg-gray-50/80 mx-6 mb-6 px-4 py-4 border-2 border-transparent focus-within:border-blue-500/50 focus-within:bg-white/90 transition-all duration-200 rounded-xl"
                                onClick={handleSearchContentClick}
                            >
                                <svg className="w-6 h-6 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <input
                                    type="text"
                                    placeholder={t.searchPlaceholder}
                                    className="bg-transparent outline-none text-gray-700 placeholder-gray-400 flex-1 text-lg font-medium"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    onFocus={handleInputFocus}
                                    onClick={handleSearchContentClick}
                                    autoFocus
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                {isLoading && (
                                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-500 flex-shrink-0"></div>
                                )}
                            </div>

                            {/* Popular Searches Section */}
                            <div className="max-w-[90%] mx-auto mb-6">
                                <h2 className="text-3xl font-bold text-white mb-6">{t.categories}</h2>

                                {/* Category Tags */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                    <button
                                        onClick={() => handleCategoryClick(1)}
                                        className="text-white rounded-lg py-2 bg-white/10 backdrop-blur-md p-2.5 border border-white/20 hover:bg-white/15 transition-all duration-200"
                                    >
                                        {t["Dizelli transportlar"]}
                                    </button>

                                    <button
                                        onClick={() => handleCategoryClick(2)}
                                        className="text-white rounded-lg py-2 bg-white/10 backdrop-blur-md p-2.5 border border-white/20 hover:bg-white/15 transition-all duration-200"
                                    >
                                        {t["Elektri transportlar"]}
                                    </button>

                                    <button
                                        onClick={() => handleCategoryClick(3)}
                                        className="text-white rounded-lg py-2 bg-white/10 backdrop-blur-md p-2.5 border border-white/20 hover:bg-white/15 transition-all duration-200 hover:scale-105 text-center text-sm"
                                    >
                                        {t["Benzinli transportlar"]}
                                    </button>

                                    <button
                                        onClick={() => handleCategoryClick(5)}
                                        className="text-white rounded-lg py-2 bg-white/10 backdrop-blur-md p-2.5 border border-white/20 hover:bg-white/15 transition-all duration-200 hover:scale-105 text-center text-sm"
                                    >
                                        {t["Elektr pallet yuk mashinasi"]}
                                    </button>

                                    <button
                                        onClick={() => handleCategoryClick(6)}
                                        className="text-white rounded-lg py-2 bg-white/10 backdrop-blur-md p-2.5 border border-white/20 hover:bg-white/15 transition-all duration-200 hover:scale-105 text-center text-sm"
                                    >
                                        {t["Elektr stacker mashinaslari"]}
                                    </button>

                                    <button
                                        onClick={() => handleCategoryClick(7)}
                                        className="text-white rounded-lg py-2 bg-white/10 backdrop-blur-md p-2.5 border border-white/20 hover:bg-white/15 transition-all duration-200 hover:scale-105 text-center text-sm"
                                    >
                                        {t["Reach truck forklift"]}
                                    </button>

                                    <button
                                        onClick={() => handleCategoryClick(8)}
                                        className="text-white rounded-lg py-2 bg-white/10 backdrop-blur-md p-2.5 border border-white/20 hover:bg-white/15 transition-all duration-200 hover:scale-105 text-center text-sm"
                                    >
                                        {t["Handle pallet forklift"]}
                                    </button>

                                    <button
                                        onClick={() => handleCategoryClick(9)}
                                        className="text-white rounded-lg py-2 bg-white/10 backdrop-blur-md p-2.5 border border-white/20 hover:bg-white/15 transition-all duration-200 hover:scale-105 text-center text-sm"
                                    >
                                        {t["Texnika extiyot qismlarin"]}
                                    </button>
                                </div>
                            </div>

                            {/* Search Results */}
                            <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                                <SearchResults
                                    isMobile={false}
                                    searchProps={{
                                        ...searchProps,
                                        setIsSearchOpen
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* CSS Animation */}
                    <style jsx>{`
                        @keyframes slideDown {
                            from {
                                transform: translateY(-100%);
                                opacity: 0;
                            }
                            to {
                                transform: translateY(0);
                                opacity: 1;
                            }
                        }
                    `}</style>
                </div>
            )}
        </>
    );
};

export default DesktopNavbar;